import React, { useEffect, useState } from "react";
import api, { inr } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Trash2, Pencil, Star, Boxes, Copy, Check, AlertTriangle, Printer } from "lucide-react";
import ImageUploader, { absoluteImageUrl } from "../../components/ImageUploader";

const EMPTY = {
  slug: "", name: "", tagline: "", description: "", long_description: "",
  price: 0, compare_at_price: 0, category: "", category_slug: "",
  images: [], benefits: "", is_featured: false, is_bestseller: false, in_stock: true,
};

const EMPTY_VARIANT_FORM = { label: "", initial_stock: 0, low_stock_threshold: 5 };

export default function AdminProductsPage() {
  const { can } = useAuth();
  const canView = can("products.view") || can("products.manage");
  const canManageVariants = can("products.manage");
  const canReceiveStock = can("inventory.adjust_stock");

  const [products, setProducts] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  // Variants dialog
  const [variantsProduct, setVariantsProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [variantForm, setVariantForm] = useState(EMPTY_VARIANT_FORM);
  const [creatingVariant, setCreatingVariant] = useState(false);
  const [copiedBarcode, setCopiedBarcode] = useState(null);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveQty, setReceiveQty] = useState("");
  const [receiveReason, setReceiveReason] = useState("");
  const [receiving, setReceiving] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [editVariantForm, setEditVariantForm] = useState({ label: "", low_stock_threshold: 5 });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
      setLoadError(false);
    } catch (err) {
      // A failed request here (e.g. a 403 for an account without
      // products.view) used to crash the whole page with an unhandled
      // promise rejection — now it just shows a normal "no access" state.
      setLoadError(true);
    }
  };

  useEffect(() => { if (canView) load(); }, [canView]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...p,
      images: p.images || [],
      benefits: (p.benefits || []).join("\n"),
      compare_at_price: p.compare_at_price || 0,
    });
    setOpen(true);
  };

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      compare_at_price: parseFloat(form.compare_at_price) || null,
      images: Array.isArray(form.images) ? form.images : [],
      benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete this product");
    }
  };

  // ----- Variants -----
  const openVariants = async (product) => {
    setVariantsProduct(product);
    setVariantForm(EMPTY_VARIANT_FORM);
    setReceiveTarget(null);
    setEditingVariant(null);
    setLoadingVariants(true);
    try {
      const { data } = await api.get(`/admin/products/${product.id}/variants`);
      setVariants(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load variants");
    } finally {
      setLoadingVariants(false);
    }
  };

  const reloadVariants = async () => {
    if (!variantsProduct) return;
    const { data } = await api.get(`/admin/products/${variantsProduct.id}/variants`);
    setVariants(data);
  };

  const createVariant = async (e) => {
    e.preventDefault();
    if (!variantForm.label.trim()) {
      toast.error("Enter a variant label — e.g. \"One Size\", \"Small\"");
      return;
    }
    setCreatingVariant(true);
    try {
      await api.post(`/admin/products/${variantsProduct.id}/variants`, {
        label: variantForm.label.trim(),
        initial_stock: parseInt(variantForm.initial_stock, 10) || 0,
        low_stock_threshold: parseInt(variantForm.low_stock_threshold, 10) || 5,
      });
      toast.success("Variant created");
      setVariantForm(EMPTY_VARIANT_FORM);
      await reloadVariants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create variant");
    } finally {
      setCreatingVariant(false);
    }
  };

  const copyBarcode = (barcode) => {
    navigator.clipboard.writeText(barcode);
    setCopiedBarcode(barcode);
    setTimeout(() => setCopiedBarcode(null), 1500);
  };

  const printSticker = async (variantId) => {
    try {
      // A plain <a href> link can't carry the login token — it's just a
      // normal browser navigation, no way to attach the Bearer header the
      // rest of the app sends automatically. Fetching through the
      // authenticated api instance and opening the result as a local blob
      // gets the same "opens in a new tab" result with correct auth.
      const response = await api.get(`/admin/products/${variantsProduct.id}/variants/${variantId}/sticker`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
    } catch (err) {
      toast.error("Couldn't generate the sticker");
    }
  };

  const startReceive = (variant) => {
    setReceiveTarget(variant);
    setReceiveQty("");
    setReceiveReason("");
  };

  const submitReceive = async () => {
    const qty = parseInt(receiveQty, 10);
    if (!qty || qty <= 0) {
      toast.error("Enter a quantity greater than 0");
      return;
    }
    setReceiving(true);
    try {
      await api.post(`/admin/inventory/variants/${receiveTarget.id}/receive`, {
        quantity: qty,
        reason: receiveReason.trim() || null,
      });
      toast.success(`+${qty} stock received`);
      setReceiveTarget(null);
      await reloadVariants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't receive stock");
    } finally {
      setReceiving(false);
    }
  };

  const startEditVariant = (variant) => {
    setEditingVariant(variant);
    setEditVariantForm({ label: variant.label, low_stock_threshold: variant.low_stock_threshold });
  };

  const submitEditVariant = async () => {
    try {
      await api.patch(`/admin/products/${variantsProduct.id}/variants/${editingVariant.id}`, {
        label: editVariantForm.label.trim(),
        low_stock_threshold: parseInt(editVariantForm.low_stock_threshold, 10) || 5,
      });
      toast.success("Variant updated");
      setEditingVariant(null);
      await reloadVariants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update variant");
    }
  };

  if (!canView) {
    return <div className="text-sm text-welljio-muted" data-testid="products-no-access">You don't have access to this page.</div>;
  }
  if (loadError) {
    return <div className="text-sm text-welljio-muted" data-testid="products-load-error">Couldn't load products. Try refreshing.</div>;
  }

  return (
    <div data-testid="admin-products-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Catalog</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Products</h1>
        </div>
        <button data-testid="product-create-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-800">
          <Plus className="w-4 h-4" strokeWidth={1.5}/> New product
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-card-${p.slug}`} className="bg-white border border-slate-100 rounded-3xl overflow-hidden">
            <div className="aspect-[4/3] bg-welljio-tint">
              {p.images?.[0] && <img src={absoluteImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover"/>}
            </div>
            <div className="p-5">
              <p className="text-xs text-welljio-subtle">{p.category}</p>
              <h3 className="font-heading font-semibold mt-1">{p.name}</h3>
              <div className="flex items-center justify-between mt-3">
                <p className="font-medium">{inr(p.price)}</p>
                <div className="flex items-center text-xs text-welljio-muted gap-2">
                  <Star className="w-3 h-3 fill-welljio-ink text-welljio-ink" strokeWidth={1}/> {p.rating?.toFixed(1)}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button data-testid={`product-edit-${p.slug}`} onClick={() => openEdit(p)} className="flex-1 text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-welljio-tint inline-flex items-center justify-center gap-1.5"><Pencil className="w-3 h-3" strokeWidth={1.5}/> Edit</button>
                <button data-testid={`product-variants-${p.slug}`} onClick={() => openVariants(p)} className="flex-1 text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-welljio-tint inline-flex items-center justify-center gap-1.5"><Boxes className="w-3 h-3" strokeWidth={1.5}/> Variants</button>
                <button data-testid={`product-delete-${p.slug}`} onClick={() => remove(p.id)} className="text-xs rounded-full border border-slate-200 px-3 py-2 hover:bg-red-50 text-red-600"><Trash2 className="w-3 h-3" strokeWidth={1.5}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-form-dialog" className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={form.slug} onChange={change("slug")} disabled={!!editing} placeholder="Slug (e.g. neck-roll)" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent disabled:bg-welljio-tint" data-testid="product-form-slug"/>
              <input required value={form.name} onChange={change("name")} placeholder="Name" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent" data-testid="product-form-name"/>
            </div>
            <input value={form.tagline} onChange={change("tagline")} placeholder="Tagline" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent" data-testid="product-form-tagline"/>
            <textarea value={form.description} onChange={change("description")} rows={2} placeholder="Short description" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <textarea value={form.long_description} onChange={change("long_description")} rows={4} placeholder="Long description" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <div className="grid sm:grid-cols-2 gap-3">
              <input type="number" required value={form.price} onChange={change("price")} placeholder="Price (INR)" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent" data-testid="product-form-price"/>
              <input type="number" value={form.compare_at_price} onChange={change("compare_at_price")} placeholder="Compare at price" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={form.category} onChange={change("category")} placeholder="Category (display)" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
              <input required value={form.category_slug} onChange={change("category_slug")} placeholder="Category slug" className="border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-welljio-subtle">Images</label>
              <div className="mt-2">
                <ImageUploader value={form.images} onChange={(images) => setForm((f) => ({ ...f, images }))} max={8} />
              </div>
            </div>
            <textarea value={form.benefits} onChange={change("benefits")} rows={3} placeholder="Benefits (one per line)" className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"/>
            <div className="flex items-center gap-6 text-sm pt-1">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={change("is_featured")} className="accent-welljio-ink"/> Featured</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_bestseller} onChange={change("is_bestseller")} className="accent-welljio-ink"/> Bestseller</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.in_stock} onChange={change("in_stock")} className="accent-welljio-ink"/> In stock</label>
            </div>
            <button type="submit" disabled={loading} data-testid="product-form-submit" className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800 disabled:opacity-60">
              {loading ? "Saving..." : editing ? "Save changes" : "Create product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Variants — the real unit of inventory. A single-size product just
          has one variant, labeled "One Size" by convention. */}
      <Dialog open={!!variantsProduct} onOpenChange={(o) => { if (!o) setVariantsProduct(null); }}>
        <DialogContent data-testid="variants-dialog" className="max-w-xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{variantsProduct?.name} — Variants</DialogTitle>
          </DialogHeader>

          {loadingVariants ? (
            <p className="text-sm text-welljio-muted">Loading...</p>
          ) : (
            <div className="space-y-3">
              {variants.length === 0 && <p className="text-sm text-welljio-muted">No variants yet — add one below (use "One Size" if this product doesn't come in sizes/colors).</p>}
              {variants.map((v) => {
                const available = v.stock - (v.reserved || 0);
                const isLow = available <= v.low_stock_threshold;
                const isEditingThis = editingVariant?.id === v.id;
                const isReceivingThis = receiveTarget?.id === v.id;
                return (
                  <div key={v.id} data-testid={`variant-row-${v.id}`} className="border border-slate-200 rounded-2xl p-4">
                    {isEditingThis ? (
                      <div className="space-y-2">
                        <input value={editVariantForm.label} onChange={(e) => setEditVariantForm({ ...editVariantForm, label: e.target.value })} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-welljio-accent" data-testid="variant-edit-label"/>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-welljio-muted">Low-stock alert at</label>
                          <input type="number" value={editVariantForm.low_stock_threshold} onChange={(e) => setEditVariantForm({ ...editVariantForm, low_stock_threshold: e.target.value })} className="w-20 border border-slate-200 rounded-xl px-2 py-1.5 text-sm outline-none focus:border-welljio-accent" data-testid="variant-edit-threshold"/>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <button onClick={submitEditVariant} data-testid="variant-edit-save" className="bg-welljio-ink text-white rounded-full px-4 py-1.5 text-xs font-medium hover:bg-slate-800">Save</button>
                          <button onClick={() => setEditingVariant(null)} className="text-xs text-welljio-muted px-3">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-welljio-ink text-sm">{v.label}</p>
                              {isLow && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-welljio-coral bg-welljio-coralSoft px-2 py-0.5 rounded-full">
                                  <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2}/> Low stock
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <code data-testid={`variant-barcode-${v.id}`} className="text-xs font-mono bg-welljio-tint px-2 py-1 rounded-lg">{v.barcode}</code>
                              <button onClick={() => copyBarcode(v.barcode)} data-testid={`variant-copy-barcode-${v.id}`} className="text-welljio-subtle hover:text-welljio-ink" title="Copy barcode">
                                {copiedBarcode === v.barcode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => printSticker(v.id)}
                                data-testid={`variant-print-sticker-${v.id}`}
                                className="text-welljio-subtle hover:text-welljio-ink"
                                title="Print sticker"
                              >
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          {canManageVariants && (
                            <button onClick={() => startEditVariant(v)} data-testid={`variant-edit-${v.id}`} className="text-welljio-subtle hover:text-welljio-ink">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                          <div className="bg-welljio-tint/60 rounded-xl py-2">
                            <p className="text-[10px] uppercase text-welljio-subtle">On-hand</p>
                            <p className="text-sm font-semibold text-welljio-ink">{v.stock}</p>
                          </div>
                          <div className="bg-welljio-tint/60 rounded-xl py-2">
                            <p className="text-[10px] uppercase text-welljio-subtle">Reserved</p>
                            <p className="text-sm font-semibold text-welljio-ink">{v.reserved || 0}</p>
                          </div>
                          <div className="bg-welljio-tint/60 rounded-xl py-2">
                            <p className="text-[10px] uppercase text-welljio-subtle">Available</p>
                            <p className={`text-sm font-semibold ${isLow ? "text-welljio-coral" : "text-welljio-ink"}`}>{available}</p>
                          </div>
                        </div>

                        {canReceiveStock && (
                          isReceivingThis ? (
                            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                              <div className="flex items-center gap-2">
                                <input type="number" value={receiveQty} onChange={(e) => setReceiveQty(e.target.value)} placeholder="Qty received" data-testid="variant-receive-qty" className="w-28 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-welljio-accent"/>
                                <input value={receiveReason} onChange={(e) => setReceiveReason(e.target.value)} placeholder="Reason (optional)" data-testid="variant-receive-reason" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-welljio-accent"/>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={submitReceive} disabled={receiving} data-testid="variant-receive-submit" className="bg-welljio-ink text-white rounded-full px-4 py-1.5 text-xs font-medium hover:bg-slate-800 disabled:opacity-60">{receiving ? "Saving..." : "Confirm"}</button>
                                <button onClick={() => setReceiveTarget(null)} className="text-xs text-welljio-muted px-3">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => startReceive(v)} data-testid={`variant-receive-${v.id}`} className="mt-3 text-xs font-medium text-welljio-accent hover:underline">
                              + Receive stock
                            </button>
                          )
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {canManageVariants && (
                <form onSubmit={createVariant} className="border border-dashed border-slate-300 rounded-2xl p-4 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-1">Add a variant</p>
                  <input
                    value={variantForm.label}
                    onChange={(e) => setVariantForm({ ...variantForm, label: e.target.value })}
                    placeholder='Label — e.g. "One Size", "Small", "Blue"'
                    data-testid="new-variant-label"
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" value={variantForm.initial_stock} onChange={(e) => setVariantForm({ ...variantForm, initial_stock: e.target.value })} placeholder="Initial stock" data-testid="new-variant-stock" className="border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent"/>
                    <input type="number" value={variantForm.low_stock_threshold} onChange={(e) => setVariantForm({ ...variantForm, low_stock_threshold: e.target.value })} placeholder="Low-stock alert at" data-testid="new-variant-threshold" className="border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-welljio-accent"/>
                  </div>
                  <button type="submit" disabled={creatingVariant} data-testid="new-variant-submit" className="w-full bg-welljio-ink text-white rounded-full py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60">
                    {creatingVariant ? "Creating..." : "Create variant"}
                  </button>
                  <p className="text-[10px] text-welljio-subtle text-center">A unique barcode is generated automatically — nothing to type.</p>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
